import React, { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, User, Loader2 } from 'lucide-react'; // Added Loader2
import { Contact } from '../../types/chat';

interface ContactListProps {
  contacts: Contact[];
  activeContactId: string | null;
  searchTerm: string;
  onContactSelect: (contactId: string) => void;
  onSearchChange: (term: string) => void;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

const ContactList: React.FC<ContactListProps> = ({
  contacts,
  activeContactId,
  searchTerm,
  onContactSelect,
  onSearchChange,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      // Fetch more when near bottom: e.g., 80% scrolled
      if (scrollTop + clientHeight >= scrollHeight * 0.8 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <User className="w-5 h-5 text-gray-600" />
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('searchOrStartNewChat')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-100 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Contact List */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
        {isLoading && contacts.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            <Loader2 className="animate-spin h-6 w-6 mx-auto mb-2" />
            {t('loadingContacts')}
          </div>
        )}

        {!isLoading && contacts.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            {t('noContactsFound')}
          </div>
        )}

        {contacts.map((contact) => (
          <div
            key={contact.id}
            onClick={() => onContactSelect(contact.id)}
            className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition-colors ${
              activeContactId === contact.id ? 'bg-gray-100' : ''
            }`}
          >
            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <span className="text-white font-medium text-sm">{contact.avatar}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-baseline">
                <h3 className="font-medium text-gray-900 truncate">{contact.name}</h3>
                <span className="text-xs text-gray-500 flex-shrink-0 ml-2">{contact.timestamp}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <p className="text-sm text-gray-600 truncate">{contact.lastMessage}</p>
                {contact.unreadCount > 0 && (
                  <span className="bg-green-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center ml-2">
                    {contact.unreadCount}
                  </span>
                )}
              </div>
            </div>
            {contact.isOnline && (
              <div className="w-3 h-3 bg-green-500 rounded-full ml-2 flex-shrink-0"></div>
            )}
          </div>
        ))}

        {isFetchingNextPage && (
          <div className="p-4 text-center text-gray-500">
            <Loader2 className="animate-spin h-5 w-5 mx-auto mb-1" />
            {t('loadingMoreContacts')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactList;